/**
 * DisputeBanner
 * Warning banner shown when a user has an active or lost payment dispute.
 * Renders at the top of the authenticated layout to inform users about
 * subscription suspension or cancellation due to a chargeback.
 *
 * Design: Follows the same premium banner pattern as BettingTipBanner
 * with danger/warning color scheme appropriate for the severity.
 */

import type { DisputeStatus } from '@parlay-picker/shared/disputeStatus';
import {
  DISPUTE_BANNER_MESSAGES,
  shouldShowDisputeBanner,
} from '@parlay-picker/shared/disputeStatus';
import { AlertTriangle, XCircle } from 'lucide-react';
import type { ReactElement } from 'react';

interface DisputeBannerProps {
  disputeStatus: DisputeStatus | null | undefined;
}

export function DisputeBanner({ disputeStatus }: DisputeBannerProps): ReactElement | null {
  if (!disputeStatus || !shouldShowDisputeBanner(disputeStatus)) return null;

  const message = DISPUTE_BANNER_MESSAGES[disputeStatus];
  const isLost = disputeStatus === 'dispute_banned';
  const Icon = isLost ? XCircle : AlertTriangle;

  return (
    <div
      className={`
        relative overflow-hidden rounded-xl border
        ${
          isLost
            ? 'border-red-500/30 bg-linear-to-r from-red-500/15 via-red-500/5 to-transparent'
            : 'border-amber-500/30 bg-linear-to-r from-amber-500/15 via-amber-500/5 to-transparent'
        }
      `}
    >
      {/* Left accent stripe */}
      <div
        className={`absolute left-0 top-0 bottom-0 w-0.75 ${isLost ? 'bg-red-500' : 'bg-amber-500'}`}
      />

      <div className="flex items-start gap-3 pl-4 pr-3 py-3.5">
        {/* Icon circle */}
        <div
          className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${
            isLost ? 'bg-red-500/15' : 'bg-amber-500/15'
          }`}
        >
          <Icon className={`w-4.5 h-4.5 ${isLost ? 'text-red-500' : 'text-amber-500'}`} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <p
            className={`text-sm font-semibold tracking-wide mb-0.5 ${isLost ? 'text-red-400' : 'text-amber-400'}`}
          >
            {message.title}
          </p>
          <p className="text-xs text-theme-text-secondary leading-relaxed">{message.description}</p>
        </div>
      </div>
    </div>
  );
}
